<script>
	import { Button, Col, Row } from "@sveltestrap/sveltestrap";
	import ErrorMessageHandler from "../../../../components/ErrorMessageHandler/ErrorMessageHandler.svelte";
	import PageHeader from "../../../../components/PageHeader/PageHeader.svelte";
	import { errorMessage, loading } from "../../../../store/store";
	import { navigate } from "svelte-routing";
	import ConfirmModal from "src/components/CustomModal/ConfirmModal.svelte";
	import CustomBreadcrumb from "src/components/CustomBreadcrumb/CustomBreadcrumb.svelte";
	import CaseInformation from "../CaseForm/CaseInformation/CaseInformation.svelte";
	import VehicleInformation from "../CaseForm/VehicleInformation/VehicleInformation.svelte";
	import DamageInformation from "../CaseForm/DamageInformation/DamageInformation.svelte";
	import Quotation from "../CaseForm/Quotation/Quotation.svelte";
	import Others from "../CaseForm/Others/Others.svelte";
	import {
		accidentGIAReportNo,
		actualRepairCost,
		additionalPhotoFiles,
		assetsOwner,
		attribution,
		carMake,
		carModel,
		caseType,
		claimType,
		damageCause,
		damageCauseType,
		dateReported,
		finalAmount,
		liabilityType,
		noOfRepairDays,
		remarks,
		selectedSides,
		staffContact,
		staffName,
		thirdPartyContact,
		thirdPartyName,
		vehicleId,
		vehicleNumber,
		workshopInvoiceFiles,
		linkToCases,
		zendeskTicketId,
		caseCreationForm,
		resetFields,
		incidentDateTime,
		additionalMediaFiles,
		vqp,
		tnsRemarks,
	} from "src/validators/CaseCreation";
	import {
		collateVideoUrls,
		createManualCase,
		postVideoUrlToCase,
		videoUpload,
	} from "src/services/fmsTasking";
	import { scrollToTop } from "src/utils/misc";
	import { onMount } from "svelte";
	import { costBreakdownInitialValues } from "src/utils/options";
	import { convertToUtc } from "src/services/helpers";

	export let isEdit;

	let load = false;
	let err;
	let selectedVehicleOption = null;
	let isOpen = false;
	let state = "create";

	$: costBreakdownItems = [];

	$: isInvalid =
		!$vehicleId.value ||
		(!$actualRepairCost.value && $actualRepairCost.value !== 0) || // allow 0 actual cost
		!$caseType.value ||
		!$claimType.value ||
		!$liabilityType.value ||
		(!$noOfRepairDays.value && $noOfRepairDays.value !== 0) || // allow 0 repair days
		!$damageCauseType.value ||
		!$attribution.value ||
		!$remarks.value ||
		$selectedSides.value.length === 0 ||
		costBreakdownItems.some((item) => item.value === undefined) ||
		new Date($incidentDateTime.value) > new Date() ||
		!$incidentDateTime.value ||
		(!$vqp.value && parseInt($vqp.value)) ||
		!$additionalMediaFiles.value ||
		$additionalMediaFiles.value.length === 0;

	$: userDamageInvalid = $attribution.value === "User" && !$damageCause.value;
	$: staffDamageInvalid =
		$attribution.value === "Staff" &&
		(!$staffName.value || !$staffContact.value);
	$: thirdPartyDamageInvalid =
		$attribution.value === "ThirdParty" &&
		(!$thirdPartyName.value || !$thirdPartyContact.value);

	$: bidError = "";

	// when vehicle selected from dropdown, get other vehicle details (e.g. asset owner)

	// handle create case submission
	async function handleSubmit() {
		bidError = "";
		const formData = new FormData();
		let videoFiles = [];
		if ($attribution.value === "User") {
			// User
			formData.append("bookingNo", $damageCause.value);
		}
		if ($attribution.value === "Staff") {
			//Staff
			formData.append("staffName", $staffName.value);
			formData.append("staffContact", $staffContact.value);
		}
		if ($attribution.value === "ThirdParty") {
			// ThirdParty
			formData.append("thirdPartyName", $thirdPartyName.value);
			formData.append("thirdPartyContact", $thirdPartyContact.value);
		}
		if ($zendeskTicketId.value) {
			formData.append("TicketId", $zendeskTicketId.value);
		}
		if (
			$accidentGIAReportNo.value &&
			$caseType.value?.value === "Accident"
		) {
			formData.append("GIAReportNo", $accidentGIAReportNo.value);
		}
		formData.append("VehicleId", $vehicleId.value);
		formData.append(
			"actualCost",
			parseFloat($actualRepairCost.value).toFixed(2)
		);
		formData.append(
			"IncidentDateTime",
			convertToUtc($incidentDateTime.value)
		);
		formData.append("CaseType", $caseType.value.value);
		formData.append("ClaimType", $claimType.value.value);
		formData.append("liabilityStatus", $liabilityType.value.value);
		formData.append("repairDays", parseInt($noOfRepairDays.value) || 0);
		formData.append("collisionType", $damageCauseType.value.value);
		formData.append("attribution", $attribution.value);
		formData.append("remarks", $remarks.value);
		$selectedSides.value.forEach((side, idx) => {
			formData.append(`damageLocation[${idx}]`, side);
		});
		$linkToCases.value.forEach((caseId, idx) => {
			formData.append(`LinkToCase[${idx}]`, caseId);
		});
		costBreakdownItems.forEach((item, idx) => {
			if (item.value !== undefined) {
				formData.append(`PriceQuotation[${idx}].item`, item.item);
				formData.append(
					`PriceQuotation[${idx}].cost`,
					item.value ? parseFloat(item.value).toFixed(2) : 0
				);
			}
		});
		formData.append(
			"finalAmount",
			costBreakdownItems
				.reduce(
					(total, item) =>
						total + (item.value ? parseFloat(item.value) : 0),
					0
				)
				.toFixed(2)
		);
		$workshopInvoiceFiles.value.forEach((file) =>
			formData.append("WorkshopInvoice", file, file.name)
		);
		$additionalMediaFiles.value.forEach((file) => {
			if (file.type.includes("video/")) {
				// video logic here
				videoFiles.push(file);
			} else {
				formData.append("AdditionalPhotos", file, file.name);
			}
		});

		formData.append("vqp", $vqp.value);
		try {
			loading.set(true);
			const res = await createManualCase(formData);
			if (res.status === 200) {
				if (videoFiles.length > 0) {
					const urlArrs = await collateVideoUrls(videoFiles);
					if (urlArrs.length > 0) {
						await postVideoUrlToCase(res.data.id, urlArrs);
					}
				}

				resetFields();
				() => (costBreakdownItems = []);
				isOpen = false;
				navigate("/issues/case-management?tab=Investigating");
			}
		} catch (err) {
			console.error(err);
			scrollToTop();
			if (err.response?.data?.message.includes("BID")) {
				bidError =
					"Invalid booking ID, please enter another booking ID";
			}
		} finally {
			loading.set(false);
		}
	}

	loading.subscribe((value) => {
		load = value;
	});

	errorMessage.subscribe((value) => {
		err = value;
	});

	if (typeof window !== "undefined") {
		window.addEventListener("popstate", () => {
			resetFields();
		});
	}

	onMount(() => {
		resetFields();
		costBreakdownItems = costBreakdownInitialValues.map((item) => ({
			...item,
			value: undefined,
		}));
		$linkToCases.value = [];
	});
</script>

<CustomBreadcrumb
	links={[
		{ label: "Case Management", to: "/issues/case-management" },
		{ label: "Create New Case" },
	]}
/>
<Row>
	<Col>
		<ErrorMessageHandler message={err} />
		<div>
			<PageHeader title={`Create New Case`} />
			<CaseInformation {zendeskTicketId} {linkToCases} {state} />
			<VehicleInformation
				{selectedVehicleOption}
				form={caseCreationForm}
				{vehicleId}
				{vehicleNumber}
				{assetsOwner}
				{carMake}
				{carModel}
				{state}
			/>
			<DamageInformation
				form={caseCreationForm}
				{dateReported}
				{attribution}
				{damageCause}
				{damageCauseType}
				{staffName}
				{staffContact}
				{thirdPartyName}
				{thirdPartyContact}
				{bidError}
				{state}
				{incidentDateTime}
				{vqp}
			/>
			<Quotation
				form={caseCreationForm}
				{caseType}
				{accidentGIAReportNo}
				{claimType}
				{selectedSides}
				{liabilityType}
				{noOfRepairDays}
				{actualRepairCost}
				{remarks}
				{costBreakdownItems}
				{state}
				{tnsRemarks}
			/>
			<Others
				{workshopInvoiceFiles}
				{additionalPhotoFiles}
				{additionalMediaFiles}
				{state}
			/>
			<Row>
				<Col class="d-flex justify-content-end gap-3">
					<Button
						class="themeButton backButton"
						on:click={() => window.history.back()}
					>
						Back
					</Button>

					<Button
						class="themeButton primary"
						on:click={() => {
							isOpen = true;
						}}
						disabled={isInvalid ||
							userDamageInvalid ||
							staffDamageInvalid ||
							thirdPartyDamageInvalid ||
							load}
					>
						Create
					</Button>
				</Col>
			</Row>
		</div>
	</Col>
</Row>
<ConfirmModal
	open={isOpen}
	toggle={() => (isOpen = false)}
	header="Create case"
	bodyText="You are about to create a new case. Are you sure you want to proceed?"
	buttonColor="primary"
	buttonText="Confirm"
	outline={false}
	{handleSubmit}
/>
